// /docs/evidence — credibility claims.
// Redacted screenshots of the run report cover + the evidence panel.
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const CLAIMS = [
    {
        label: "Validated-vs-provisional registry",
        body:
            "Every parameter is tagged at registration time with a confidence " +
            "class — reviewed, provisional, or imported — and every downstream " +
            "record preserves that tag unchanged. A run composed entirely of " +
            "provisional inputs cannot accidentally masquerade as validated.",
    },
    {
        label: "Citation-first",
        body:
            "Parameters with sources outside our own engineering ship with the " +
            "reference embedded (literature, a published airframe spec, or a " +
            "customer-supplied document). The reference follows the parameter " +
            "into every run report that uses it.",
    },
    {
        label: "Bit-exact replay (kinematic)",
        body:
            "For kinematic runs, the engine is fully deterministic — same " +
            "inputs, same outputs, byte-for-byte. A replay is used in regression " +
            "testing so a new release cannot silently change a trajectory.",
    },
    {
        label: "Independently verified JSBSim",
        body:
            "Flight-dynamics backend uses JSBSim, an independently maintained " +
            "6-DoF library with decades of aerospace provenance. We do not " +
            "modify the core solver; we stitch airframe, environment, and " +
            "control integrations around it.",
    },
    {
        label: "Reviewer chain",
        body:
            "Analysts submit; reviewers approve, request changes with notes, or " +
            "reject. Only approved runs can be promoted to golden references. " +
            "Every state transition is audited with user id and timestamp.",
    },
    {
        label: "Immutable run snapshot",
        body:
            "At submit-time, every parameter that contributed to the run is " +
            "frozen into an immutable snapshot on the run record. The snapshot " +
            "survives later edits to the underlying library blocks — what ran " +
            "was what ran.",
    },
];

export default function DocsEvidencePage() {
    return (
        <>
            <SeoHead path="/docs/evidence"
                     title="UAVSimNet Docs — Evidence & credibility"
                     description="Validated-vs-provisional registry, citations, bit-exact replay, JSBSim, reviewer chain, immutable run snapshots." />
            <div data-testid="docs-evidence-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// DOCS · 3 OF 4</span>
                        <h1 className="section__heading">Evidence & credibility</h1>
                        <p className="section__lede">
                            A simulation platform is only as useful as the evidence
                            trail it produces. UAVSimNet is built so that every
                            claim in a run report is traceable back to a named
                            source, a confidence class, and an approval event.
                        </p>

                        <figure
                            data-testid="docs-evidence-cover"
                            style={{
                                marginTop: 32,
                                padding: 16,
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 8,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/screens/run_report_cover_redacted.svg"
                                alt="Redacted run report cover showing title, approval state, reviewer, and parameter provenance table headings."
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                            />
                            <figcaption
                                style={{
                                    marginTop: 10,
                                    color: "var(--ux-text-3)",
                                    fontSize: 12,
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                            >
                                Run report cover. Identifiers, parameter values,
                                and reviewer names redacted; structure preserved.
                            </figcaption>
                        </figure>

                        <div
                            style={{
                                marginTop: 44,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: 18,
                            }}
                        >
                            {CLAIMS.map((c) => (
                                <div
                                    key={c.label}
                                    className="cap-card"
                                    data-testid={`docs-evidence-${c.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                >
                                    <div
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-text-1)",
                                            fontSize: 13,
                                            marginBottom: 8,
                                        }}
                                    >
                                        {c.label}
                                    </div>
                                    <p
                                        style={{
                                            color: "var(--ux-text-3)",
                                            fontSize: 13,
                                            lineHeight: 1.55,
                                            margin: 0,
                                        }}
                                    >
                                        {c.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <figure
                            data-testid="docs-evidence-panel"
                            style={{
                                marginTop: 44,
                                padding: 16,
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 8,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/screens/evidence_panel_redacted.svg"
                                alt="Redacted Evidence rail showing Blocking / Warnings / Provenance groups with counts and sample row shapes."
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                            />
                            <figcaption
                                style={{
                                    marginTop: 10,
                                    color: "var(--ux-text-3)",
                                    fontSize: 12,
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                            >
                                Evidence rail — surfaces blocking issues, warnings,
                                and the parameter-provenance ledger side-by-side
                                with the edit pane.
                            </figcaption>
                        </figure>

                        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
                            <Link to="/docs/use-cases" className="btn-primary" data-testid="docs-evidence-next">
                                Next · Use cases <ArrowRight size={14} />
                            </Link>
                            <Link to="/docs" className="btn-ghost" data-testid="docs-evidence-back">
                                Back to docs index
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
